import AuthController, { UserInformation } from "./AuthController";
import appRoutes, { publicRoutes } from "../utils/constants/appRoutes";
import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { NextRouter, useRouter } from "next/router";
import { useSWRConfig } from "swr";
import { __dev__, __preview__ } from "../utils";
import {
  REGIONAL_INSTANCE_CONFIG,
  RegionalInstanceId,
} from "../lib/[services]/regional-instance";

function isWootingDomainRedirect(path: string) {
  if (
    path.startsWith(
      REGIONAL_INSTANCE_CONFIG[RegionalInstanceId.Global].endpoints.store
    ) ||
    path.startsWith(
      REGIONAL_INSTANCE_CONFIG[RegionalInstanceId.CN].endpoints.store
    )
  ) {
    return true;
  }

  if (__dev__ || __preview__) {
    if (
      path.startsWith("https://wooting.dev") ||
      /^http:\/\/localhost:\d{1,5}($|\/)/.test(path)
    ) {
      return true;
    }
  }

  return false;
}

function isDisallowedRelativeRedirect(path: string) {
  if (
    path.startsWith(appRoutes.LOGIN_PAGE) ||
    path.startsWith(appRoutes.RESET_PASSWORD)
  ) {
    return true;
  }

  return false;
}

function afterLogin(router: NextRouter) {
  const isLogin =
    router.pathname.startsWith(appRoutes.LOGIN_PAGE) ||
    router.pathname.startsWith(appRoutes.RESET_PASSWORD);

  if (isLogin) {
    const query = new URL(window.location.toString()).searchParams;
    const next = query.get("next");
    if (
      typeof next === "string" &&
      (isWootingDomainRedirect(next) ||
        (next.startsWith("/") && !isDisallowedRelativeRedirect(next)))
    ) {
      // This way we only redirect to our store website or a valid relative route
      router.push(next);
    } else {
      router.push(appRoutes.HOME_PAGE);
    }
  }
}

function afterLogout(router: NextRouter) {
  const pathname = router.pathname;
  if (!publicRoutes.includes(pathname)) {
    const redirect =
      pathname === appRoutes.HOME_PAGE
        ? appRoutes.LOGIN_PAGE
        : appRoutes.LOGIN_PAGE + `?next=${encodeURIComponent(pathname)}`;

    router.replace(redirect);
  }
}

export function useUser(): UseQueryResult<UserInformation | null> {
  const router = useRouter();
  return useQuery({
    queryKey: ["test-auth"],
    queryFn: () => AuthController.testAuth(),
    staleTime: 10 * 60 * 1000, // 10 minutes
    select: (user) => {
      if (user) {
        afterLogin(router);
      } else {
        afterLogout(router);
      }
      return user;
    },
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  const { mutate } = useSWRConfig();
  return useMutation({
    mutationFn: (credentials: { email: string; password: string }) =>
      AuthController.login(credentials),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["test-auth"] });

      // invalid all SWR caches

      mutate(() => true, undefined, { revalidate: false });
    },
  });
}

export function useOTPLogin() {
  const queryClient = useQueryClient();
  const { mutate } = useSWRConfig();
  return useMutation({
    mutationFn: (credentials: { phonenumber: string; otpCode: string }) =>
      AuthController.loginOTP(credentials),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["test-auth"] });

      // invalid all SWR caches

      mutate(() => true, undefined, { revalidate: false });
    },
  });
}

export function useOTPSend() {
  const queryClient = useQueryClient();
  const { mutate } = useSWRConfig();
  return useMutation({
    mutationFn: (phonenumber: string) => AuthController.sendOTP(phonenumber),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["test-auth"] });

      // invalid all SWR caches

      mutate(() => true, undefined, { revalidate: false });
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  const { mutate } = useSWRConfig();
  return useMutation({
    mutationFn: () => AuthController.logout(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["test-auth"] });

      // invalid all SWR caches
      mutate(() => true, undefined, { revalidate: false });
    },
  });
}
